import { isValidGrossMarginPercentage } from '@/utils/pricing-validation';
import { isValidNumber } from '@/utils/number';
import { PricingCalculations } from '@/utils/pricing-calculations';
import { PricingItemManagementTableRow } from '@sales-portal/domain/pricing/screens/management/types';
import { VariantPackCostChange } from '@/experiences/sales-portal/domain/pricing/screens/management/blocs/transformers/use-pricing-management-transformers';
import {
  VariantPackCostChangeDataFieldsFragment,
  VariantPackDetailedCostChangeDataFieldsFragment,
} from '@/types';

type CostChangeFragment =
  | VariantPackCostChangeDataFieldsFragment
  | VariantPackDetailedCostChangeDataFieldsFragment;

/**
 * Pricing utilities specific to VariantPackCostChangeData.
 * These functions extract and calculate pricing values from variant pack row data.
 *
 * For core pricing math (not tied to a specific data type), see:
 * @/utils/pricing-calculations
 */

/** Round a percentage delta to 1 decimal place, normalizing ±0 to 0. */
export const roundPercentageToNearestTenth = (value: number): number =>
  parseFloat(value.toFixed(1)) || 0;

/** Last (pre-change) price = current price − price delta. */
export const getLastPriceInMicros = (
  row: Pick<VariantPackCostChange, 'priceDeltaInMicros' | 'priceInMicros'>,
): number => row.priceInMicros - row.priceDeltaInMicros;

/** Last (pre-change) cost = current cost − cost delta. */
export const getLastCostInMicros = (
  row: Pick<VariantPackCostChange, 'costDeltaInMicros' | 'costInMicros'>,
): number => row.costInMicros - row.costDeltaInMicros;

/**
 * A delta as a percentage of the value it moved from — pair with
 * `getLastCostInMicros` / `getLastPriceInMicros`. null when that value is 0,
 * so the row drops out of a range filter's bounds instead of going infinite.
 */
export const percentOfPrevious = (
  deltaInMicros: number,
  previousInMicros: number,
): number | null => {
  if (
    !isValidNumber(deltaInMicros) ||
    !isValidNumber(previousInMicros) ||
    previousInMicros === 0
  ) {
    return null;
  }

  return (deltaInMicros / previousInMicros) * 100;
};

/** Last (pre-change) GM% = current GM% − GM% delta. */
export const getLastGrossMarginPercentage = (
  row: Pick<
    VariantPackCostChange,
    'grossMarginPercentage' | 'grossMarginPercentageDelta'
  >,
): number => row.grossMarginPercentage - row.grossMarginPercentageDelta;

/**
 * Calculates the price in micros based on cost and target gross margin percentage.
 *
 * @param row - The pricing data containing cost information.
 * @param grossMarginPercentage - The target gross margin percentage (e.g., 20 for 20%).
 * @returns The calculated price in micros, or null if required data is missing or if margin is 100% or higher.
 */
export const getPriceByCostAndMargin = (
  row: PricingItemManagementTableRow,
  grossMarginPercentage: number | null | undefined,
): number | null => {
  if (
    !isValidNumber(row.costInMicros) ||
    !isValidGrossMarginPercentage(grossMarginPercentage)
  ) {
    return null;
  }

  // Convert 10.45 to 0.1045
  const marginDecimal = grossMarginPercentage / 100;

  return Math.round(row.costInMicros / (1 - marginDecimal));
};

/**
 * Calculates Previous Price mathematically using the trusted GP Delta.
 * Formula: Previous Price = Previous GP + Previous Cost
 *
 * @param row - The pricing data for a variant pack.
 * @returns The calculated previous price in micros, or null if any required field is missing.
 */
export const getPreviousPriceActual = (
  row: VariantPackCostChange,
): number | null => {
  const gpDelta = row.grossProfitDeltaInMicros;
  const priceInMicros = row.priceInMicros;
  if (
    !isValidNumber(priceInMicros) ||
    !isValidNumber(row.costInMicros) ||
    !isValidNumber(row.costDeltaInMicros) ||
    !isValidNumber(gpDelta)
  ) {
    return null;
  }

  const currentGP = priceInMicros - row.costInMicros;
  const previousGP = currentGP - gpDelta;
  const previousCost = row.costInMicros - row.costDeltaInMicros;

  return previousGP + previousCost;
};

/**
 * Calculates the Gross Margin Percentage delta based on ACTUAL data.
 * Formula: GM% delta = Current GM% - Previous GM%
 *
 * @param row - The pricing data for a variant pack.
 * @param previousPriceInMicros - The calculated previous price (optional).
 * @returns The delta in gross margin percentage as a percentage, or null if data is missing.
 */
export const getGrossMarginPercentageDeltaByRow = (
  row: VariantPackCostChange,
  previousPriceInMicros?: number | null,
): number | null => {
  if (
    !isValidNumber(row.costInMicros) ||
    !isValidNumber(row.costDeltaInMicros)
  ) {
    return null;
  }

  const previousCost = row.costInMicros - row.costDeltaInMicros;
  const previousPrice = previousPriceInMicros ?? getPreviousPriceActual(row);
  const currentGrossMarginPercent = row.grossMarginPercentage;

  if (
    !isValidNumber(previousPrice) ||
    !isValidNumber(currentGrossMarginPercent) ||
    previousPrice === 0
  ) {
    return null;
  }

  // Calculate Previous GM%
  const previousGrossMarginPercentage =
    PricingCalculations.grossProfitPercentFromPrice(
      previousPrice,
      previousCost,
    );

  if (previousGrossMarginPercentage === null) {
    return null;
  }

  return currentGrossMarginPercent - previousGrossMarginPercentage;
};

/**
 * Calculates the edit-only weekly Gross Profit impact: the GP/unit the edit
 * moved (relative to the current, post-cost-change value) × the volume at edit
 * time. The cost-change portion is excluded — net impact is CC + this.
 *
 * @param originalRow - The original (pre-edit, post-cost-change) pricing data.
 * @param rowWithUpdates - The row data including user updates.
 * @returns The edit-only weekly impact in micros, or null if not calculable.
 */
export const getEditWeeklyGrossProfitImpactInMicros = (
  originalRow: PricingItemManagementTableRow,
  rowWithUpdates: PricingItemManagementTableRow,
): number | null => {
  const currentGP = rowWithUpdates.grossProfitInMicros;
  const originalGP = originalRow.grossProfitInMicros ?? 0;
  const updatedGPDelta = isValidNumber(currentGP)
    ? currentGP - originalGP
    : null;

  return isValidNumber(updatedGPDelta)
    ? updatedGPDelta * originalRow.quantity
    : null;
};

/**
 * Calculates Gross Profit in micros for a fragment.
 *
 * @param variantPackDetailedFragment - The GraphQL fragment data.
 * @returns Gross Profit in micros, or null.
 */
export const getGrossProfitInMicros = (
  variantPackDetailedFragment: CostChangeFragment,
): number | null => {
  return PricingCalculations.grossProfitAmountFromPrice(
    variantPackDetailedFragment.price_in_micros,
    variantPackDetailedFragment.cost_in_micros,
  );
};

/**
 * Calculates Previous Cost in micros for a fragment.
 * Formula: previous cost = current cost - cost delta
 *
 * @param variantPackDetailedFragment - The GraphQL fragment data.
 * @returns Previous Cost in micros, or null.
 */
const getPreviousCostInMicros = (
  variantPackDetailedFragment: CostChangeFragment,
): number | null => {
  if (
    isValidNumber(variantPackDetailedFragment.cost_in_micros) &&
    isValidNumber(variantPackDetailedFragment.cost_in_micros_delta)
  ) {
    return (
      variantPackDetailedFragment.cost_in_micros -
      variantPackDetailedFragment.cost_in_micros_delta
    );
  }

  return null;
};

/**
 * Calculates Previous Price in micros for a fragment.
 * Formula: previous price = current price - price delta
 *
 * @param variantPackDetailedFragment - The GraphQL fragment data.
 * @returns Previous Price in micros, or null.
 */
const getPreviousPriceInMicros = (
  variantPackDetailedFragment: CostChangeFragment,
): number | null => {
  if (
    isValidNumber(variantPackDetailedFragment.price_in_micros) &&
    isValidNumber(variantPackDetailedFragment.price_in_micros_delta)
  ) {
    return (
      variantPackDetailedFragment.price_in_micros -
      variantPackDetailedFragment.price_in_micros_delta
    );
  }

  return null;
};

/**
 * Calculates Gross Profit delta in micros for a fragment.
 * Formulas:
 * GP delta = current GP - previous GP
 * previous GP = previous price - previous cost
 *
 * @param variantPackDetailedFragment - The GraphQL fragment data.
 * @returns Gross Profit delta in micros, or null.
 */
export const getGrossProfitDeltaInMicros = (
  variantPackDetailedFragment: CostChangeFragment,
): number | null => {
  const currentGrossProfit = getGrossProfitInMicros(
    variantPackDetailedFragment,
  );
  const currentPrice = variantPackDetailedFragment.price_in_micros;
  const previousCost = getPreviousCostInMicros(variantPackDetailedFragment);
  const previousPrice = getPreviousPriceInMicros(variantPackDetailedFragment);

  if (
    isValidNumber(currentPrice) &&
    isValidNumber(previousPrice) &&
    isValidNumber(previousCost) &&
    isValidNumber(currentGrossProfit)
  ) {
    const previousGrossProfit = previousPrice - previousCost;

    return currentGrossProfit - previousGrossProfit;
  }

  return null;
};

/**
 * Calculates Gross Margin Percentage (ratio) for a fragment.
 *
 * @param variantPackDetailedFragment - The GraphQL fragment data.
 * @returns Gross Margin Percentage  or null.
 */
export const getGrossMarginPercentage = (
  variantPackDetailedFragment: CostChangeFragment,
): number | null => {
  if (isValidNumber(variantPackDetailedFragment.gross_profit_percentage)) {
    return variantPackDetailedFragment.gross_profit_percentage;
  }

  const grossProfit = getGrossProfitInMicros(variantPackDetailedFragment);
  const price = variantPackDetailedFragment.price_in_micros;

  if (isValidNumber(grossProfit) && isValidNumber(price) && price !== 0) {
    return (grossProfit / price) * 100;
  }

  return null;
};

/**
 * Calculates Gross Margin Percentage delta for a fragment.
 * Uses the API-provided gross_profit_percentage for current GM% (consistent
 * with the displayed GM% column), and calculates previous GM% from
 * previous price/cost.
 *
 * @param variantPackDetailedFragment - The GraphQL fragment data.
 * @returns Gross Margin Percentage delta (as whole-number percentage), or null.
 */
export const getGrossMarginPercentageDelta = (
  variantPackDetailedFragment: CostChangeFragment,
): number | null => {
  const currentGrossMarginPercentage = getGrossMarginPercentage(
    variantPackDetailedFragment,
  );
  const previousCost = getPreviousCostInMicros(variantPackDetailedFragment);
  const previousPrice = getPreviousPriceInMicros(variantPackDetailedFragment);

  const previousGrossMarginPercentage =
    PricingCalculations.grossProfitPercentFromPrice(
      previousPrice,
      previousCost,
    );

  if (
    isValidNumber(currentGrossMarginPercentage) &&
    isValidNumber(previousGrossMarginPercentage)
  ) {
    return currentGrossMarginPercentage - previousGrossMarginPercentage;
  }

  return null;
};
